import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEventIndicator } from "../../../core/components/eventIndicator/useEventIndicator";
import { getLoggedIn, getUserId } from "../../../core/store/appSlice";
import {
  getIsReleaseSession,
  getPracticeFormIdsArray,
  getPracticePatientId,
  getStaffFillDashboardOrigin,
} from "../../../core/store/currentFormSlice";
import { State, useMainStore } from "../../../core/store/mainStore";
import { isFramed, STAFF_FILL_MESSAGE } from "../../../core/utils/staffFillFrame";
import { handleLogout } from "./handleLogout";

/** Mirrors PATIENT_WEBAPP_SESSION_MAX_AGE_SECONDS on the server and the 3600 s in the rules. */
export const WEB_APP_SESSION_MAX_AGE_MS = 60 * 60 * 1000;

/** The end screen of a practice release session (PF-6133). */
export const RELEASE_DONE_PATH = "/release/done";
/** The frame entry of a staff-fill session. */
export const STAFF_FILL_PATH = "/release/staff";

/**
 * Router state of the end screen: `ended` for every exit except the completion of the link's last form, and the
 * dashboard origin of a staff-fill session, read before the logout cleared the scope.
 */
export type ReleaseDoneRouteState = { ended?: boolean; dashboardOrigin?: string };

const SESSION_STARTED_AT_KEY = "pf_web_session_started_at";
let sessionStartedAtInMemory: number | undefined;

/**
 * Called once the session is signed in. Device time on both ends of the timer, never the server's
 * auth_time against the device clock: a tablet whose clock is off would otherwise end every session.
 */
export const markWebAppSessionStart = () => {
  sessionStartedAtInMemory = Date.now();
  try {
    sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(sessionStartedAtInMemory));
  } catch {
    // the memory copy
  }
};

/** Undoes a mark for an attempt that never became the tab's session, so it is not mistaken for one later. */
export const clearWebAppSessionStart = () => {
  sessionStartedAtInMemory = undefined;
  try {
    sessionStorage.removeItem(SESSION_STARTED_AT_KEY);
  } catch {
    // the memory copy
  }
};

const readWebAppSessionStart = (): number | undefined => {
  try {
    const stored = Number(sessionStorage.getItem(SESSION_STARTED_AT_KEY));
    if (stored > 0) return stored;
  } catch {
    // the memory copy
  }
  return sessionStartedAtInMemory;
};

/**
 * Ends a practice release session (PF-6133) on its end screen, never on the SMS screens. MainLayout shows its
 * spinner meanwhile, so InAppLayout, whose redirect of a signed-out tab would race the logout, is unmounted, and so
 * is the record listener the sign-out would make fail. Callers read the marker first: the logout clears it.
 */
export const useEndReleaseSession = () => {
  const navigate = useNavigate();
  const setLoggingOut = useMainStore((state) => state.setLoggingOut);
  return async ({ ended }: { ended: boolean }) => {
    setLoggingOut(true);
    try {
      const dashboardOrigin = getStaffFillDashboardOrigin(useMainStore.getState());
      await handleLogout();
      const state: ReleaseDoneRouteState = {
        ...(ended ? { ended: true } : {}),
        ...(dashboardOrigin ? { dashboardOrigin } : {}),
      };
      // The spinner may only go once the end screen is rendered, or InAppLayout mounts again on /form and sends the
      // signed-out tab to /welcome. react-router renders a navigation in a transition, after the spinner's update,
      // unless flushSync renders it at once.
      await navigate(RELEASE_DONE_PATH, { replace: true, state, flushSync: true });
    } finally {
      setLoggingOut(false);
    }
  };
};

export const useExpireWebAppSession = () => {
  const navigate = useNavigate();
  const eventIndicator = useEventIndicator();
  const { t } = useTranslation();
  const endReleaseSession = useEndReleaseSession();
  return async () => {
    // The timer, a foreign link and a denied record listener end a release session on its own end screen (PF-6133).
    if (getIsReleaseSession(useMainStore.getState())) {
      await endReleaseSession({ ended: true });
      return;
    }
    await handleLogout();
    navigate("/welcome/login", { replace: true });
    eventIndicator("info", t("SMS_LOGIN_SESSION_EXPIRED"));
  };
};

/** The link the forms come from, recorded in the session's scope at login. */
export const getLinkKey = (state: State) =>
  [state.currentPracticeId ?? "", ...(getPracticeFormIdsArray(state) ?? []).map((form) => form.id)].join("|");

/**
 * The link state is shared by the device's tabs, the session is not: after a reload, a link another tab
 * opened meanwhile would have its forms filed under this session's practice and record. The session ends
 * instead. Returns true until the sign-out has happened.
 */
export const useEndSessionOnForeignLink = () => {
  const expire = useExpireWebAppSession();
  const linkChanged = useMainStore(
    (state) => state.webAppScope !== undefined && state.webAppScope.linkKey !== getLinkKey(state),
  );
  useEffect(() => {
    if (linkChanged) void expire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkChanged]);
  return linkChanged;
};

/**
 * Signs out when the rules would start refusing, instead of letting the next write fail. Re-armed for
 * every session; a tab without a recorded start (opened before this code) gets the full hour.
 */
export const useWebAppSessionTimeout = () => {
  const expire = useExpireWebAppSession();
  const userId = useMainStore(getUserId);
  useEffect(() => {
    if (!userId) return;
    // The staff-fill entry signs a leftover session out itself; an expired one must not end the new frame.
    if (window.location.pathname.startsWith(STAFF_FILL_PATH)) return;
    const startedAt = readWebAppSessionStart() ?? Date.now();
    const timer = setTimeout(expire, Math.max(startedAt + WEB_APP_SESSION_MAX_AGE_MS - Date.now(), 0));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
};

export const useStaffFillParentMessages = () => {
  const dashboardOrigin = useMainStore(getStaffFillDashboardOrigin);
  const endReleaseSession = useEndReleaseSession();
  useEffect(() => {
    if (!dashboardOrigin || !isFramed()) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== dashboardOrigin) return;
      if ((event.data as { type?: unknown } | null)?.type !== STAFF_FILL_MESSAGE.end) return;
      void endReleaseSession({ ended: true });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardOrigin]);
};

/**
 * A release session (PF-6133) that has lost its sign-in or its record ends on the release end screen. InAppLayout
 * renders nothing meanwhile, so such a tab never reaches the chooser or the SMS screens.
 */
export const useEndReleaseSessionWithoutRecord = () => {
  const endReleaseSession = useEndReleaseSession();
  const withoutRecord = useMainStore(
    (state) => getIsReleaseSession(state) && (!getLoggedIn(state) || !getPracticePatientId(state)),
  );
  useEffect(() => {
    if (withoutRecord) void endReleaseSession({ ended: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withoutRecord]);
};
